import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardTitle } from "@/shared/ui/card";
import { LayoutDashboard, MoreVertical, Trash, User, Pencil, X } from "lucide-react";
import { Customer } from "@/shared/types/customerTypes";
import { Button } from "@/shared/ui/button";
import Dropdown from "@/shared/components/Dropdown";
import { Badge } from "@/shared/ui/badge";
import { useUser } from "@/contexts/UserContext";

interface CustomerCardProps {
  customer: Customer;
  onRemoveOrDeleteCustomer?: (isDelete: boolean) => void;
  onViewCustomer?: () => void;
  onManageCustomer?: () => void;
}

const CustomerCard = ({
  customer,
  onRemoveOrDeleteCustomer,
  onViewCustomer,
  onManageCustomer,
}: CustomerCardProps) => {
  const { selectedOrganization } = useUser();
  const canRemove = customer.hasServiceLines === false;
  const canDelete = (customer.organizations?.length ?? 0) === 1 && customer.hasUsers === false;

  const dropdownMenuItems = useMemo(() => {
    const items: {
      label: string;
      icon: typeof Pencil;
      variant: "default" | "destructive";
      onClick: () => void;
    }[] = [];

    if (onManageCustomer) {
      items.push({
        label: "Manage",
        icon: Pencil,
        variant: "default",
        onClick: onManageCustomer,
      });
    }

    if (onRemoveOrDeleteCustomer && (canRemove || canDelete)) {
      items.push({
        label: canDelete ? "Delete" : "Remove",
        icon: canDelete ? Trash : X,
        variant: "destructive",
        onClick: () => onRemoveOrDeleteCustomer(canDelete ? true : false),
      });
    }

    return items;
  }, [onManageCustomer, onRemoveOrDeleteCustomer, canRemove, canDelete]);

  return (
    <Card className="border border-border">
      <CardContent className="flex flex-col gap-4 p-4">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-background rounded-lg flex items-center justify-center border">
              <User className="h-6 w-6" />
            </div>
            <div>
              <CardTitle className="text-lg">{customer.name}</CardTitle>
              <CardDescription className="flex flex-col text-sm text-gray-500">
                {customer.identificationNumber && <span>ID: {customer.identificationNumber}</span>}
                {customer.address && <span>{customer.address}</span>}
              </CardDescription>
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">
            {selectedOrganization?.isKYCRequiredForServiceLines === true && (
              <Badge variant="outline" className={`text-xs ${customer.kycVerified ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}>
                {customer.kycVerified ? "KYC Verified" : "KYC Not Verified"}
              </Badge>
            )}
            {onViewCustomer && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewCustomer?.();
                }}
              >
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Button>
            )}
            {dropdownMenuItems.length > 0 && (
              <Dropdown triggerIcon={MoreVertical} items={dropdownMenuItems} />
            )}
          </div>
        </div>
        {customer.hasServiceLines === false && (
          <p className="text-sm text-red-500 italic">No service lines assigned to this customer yet.</p>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerCard; 
